import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';
import { lambdaService, AnalysisResult, FileInfo } from '../../services/lambdaService';
import { fetchAndDecompressS3, shouldFetchFromS3 } from '../../utils/s3Utils';
import { Progress } from '../ui/progress';
import { Spinner } from '../ui/spinner';
import { EnhancedTableViewer } from '../ui/enhanced-table-viewer';
import { TopNav } from '../ui/top-nav';
import { QueryHistory } from './QueryHistory';
import {
  Play,
  Download,
  Clock,
  Database,
  Code,
  Table as TableIcon,
  FileText,
  Sparkles,
  Bug,
  Eye,
  AlertTriangle,
  Zap,
  RotateCcw,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Search,
  BarChart3,
  Box,
  MessageSquare,
  Send,
  Bot,
  Trash2,
  Loader2
} from 'lucide-react';
import { SearchableFileSelect } from '../ui/searchable-file-select';
import { ChatMessage } from '../../types/introspect';

// Helper function to clean up display names - hide internal implementation details
const getDisplayName = (fileName: string): string => {
  let name = fileName;
  // Remove common extensions
  name = name.replace(/\.(parquet|csv|json|xlsx|xls|tsv)$/i, '');
  // Remove double extensions like .csv.parquet
  name = name.replace(/\.(csv|json)\.(parquet)$/i, '');
  // Remove timestamp prefix (e.g., "1765940893209-")
  if (/^\d+-/.test(name)) {
    name = name.replace(/^\d+-/, '');
  }
  // Remove date-time suffix (e.g., "_20231215_143022")
  name = name.replace(/_\d{8}_\d{6}$/, '');
  // Remove "(X files)" or "(X parts)" suffixes
  name = name.replace(/\s*\(\d+\s+(files?|parts?)\)$/i, '');
  // Remove "_part_X" or "_chunk_X" suffixes
  name = name.replace(/[_-](part|chunk)[_-]?\d+$/i, '');
  // Remove trailing underscores or hyphens
  name = name.replace(/[_-]+$/, '');
  return name;
};

export const AnalyzePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [currentQueryFolder, setCurrentQueryFolder] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [hasShownCompletionToast, setHasShownCompletionToast] = useState(false); // Prevent duplicate toasts
  const [rowGroupResults, setRowGroupResults] = useState<{rowGroupIndex: number, data: any[]}[]>([]); // Store results by row group
  const [viewMode, setViewMode] = useState<'paginated' | 'unified'>('paginated'); // View mode toggle
  const [currentPage, setCurrentPage] = useState(0); // Current page (row group)
  const [maxParallelism, setMaxParallelism] = useState(500); // Default parallelism for cluster processing
  const [naturalLanguagePrompt, setNaturalLanguagePrompt] = useState('');
  const [isGeneratingQuery, setIsGeneratingQuery] = useState(false);
  const [lastUsedPrompt, setLastUsedPrompt] = useState<string>('');
  const [lastGeneratedQuery, setLastGeneratedQuery] = useState<string>('');
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [selectedDebugInfo, setSelectedDebugInfo] = useState<any>(null);
  const [queryHistoryRefreshTrigger, setQueryHistoryRefreshTrigger] = useState<number>(0);
  
  // Search mode state
  const [analysisMode, setAnalysisMode] = useState<'query' | 'search'>('query');
  const [searchTerm, setSearchTerm] = useState('');
  const [executedSearchTerm, setExecutedSearchTerm] = useState('');
  
  // File search state
  const [fileSearchTerm, setFileSearchTerm] = useState('');
  
  // AI Model Configuration
  const [aiProvider, setAiProvider] = useState<'gpt-5' | 'claude' | 'bedrock'>('bedrock');
  const [claudeModel, setClaudeModel] = useState<'claude-3-5-sonnet-20241022' | 'claude-3-haiku-20240307' | 'claude-3-opus-20240229' | 'claude-haiku-4-5'>('claude-haiku-4-5');
  const [bedrockModel, setBedrockModel] = useState<'openai.gpt-oss-120b-1:0' | 'openai.gpt-oss-20b-1:0'>('openai.gpt-oss-120b-1:0');
  const [gpt5ReasoningEffort, setGpt5ReasoningEffort] = useState<'minimal' | 'low' | 'medium' | 'high'>('minimal');
  const [aiConfigCollapsed, setAiConfigCollapsed] = useState(true); // Collapsed by default
  const [parallelismConfigCollapsed, setParallelismConfigCollapsed] = useState(true); // Collapsed by default
  const [advancedQueryCollapsed, setAdvancedQueryCollapsed] = useState(true); // Collapsed by default

  // Chat state for "Talk to Your Data"
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([
    "What are the key patterns in this data?",
    "Summarize the main statistics",
    "What columns have missing values?",
    "Show me the top 10 rows",
  ]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Query Scope Configuration
  const [queryScope, setQueryScope] = useState<'first' | 'all'>('all'); // 'first' for first part only, 'all' for complete dataset

  // Processing Level Configuration - Always use 'file' (All Groups) mode
  const processingLevel: 'file' | 'rowgroup' = 'file'; // Always 'file' for All Groups mode

  // Get AI config summary for collapsed state
  const getAiConfigSummary = useCallback(() => {
    if (aiProvider === 'gpt-5') {
      return `GPT-5 (${gpt5ReasoningEffort})`;
    } else if (aiProvider === 'bedrock') {
      const modelName = bedrockModel === 'openai.gpt-oss-20b-1:0' ? 'GPT-OSS-20B' : 'GPT-OSS-120B';
      return `Secure GPT // AWS Bedrock (${modelName})`;
    } else {
      const modelName = claudeModel.includes('sonnet') ? 'Sonnet' :
                       claudeModel.includes('haiku') ? 'Haiku' : 'Opus';
      return `Claude 3.5 ${modelName}`;
    }
  }, [aiProvider, gpt5ReasoningEffort, claudeModel, bedrockModel]);

  // Memoized handlers to prevent re-renders
  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuery(e.target.value);
  }, []);

  const handleNaturalLanguagePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNaturalLanguagePrompt(e.target.value);
  }, []);

  const handleSearchTermChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleFileSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFileSearchTerm(e.target.value);
  }, []);

  // Scroll chat to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Reset chat when file changes
  useEffect(() => {
    setChatMessages([]);
    setChatInput('');
    setSuggestedQuestions([
      "What are the key patterns in this data?",
      "Summarize the main statistics",
      "What columns have missing values?",
      "Show me the top 10 rows",
    ]);
  }, [selectedFile]);

  // Handle sending chat message
  const handleSendMessage = async (message?: string) => {
    const messageToSend = message || chatInput.trim();
    if (!selectedFile || !messageToSend) return;

    // Add user message
    const userMessage: ChatMessage = { role: 'user', content: messageToSend };
    const newMessages = [...chatMessages, userMessage];
    setChatMessages(newMessages);
    setChatInput('');
    setIsSendingMessage(true);

    try {
      const result = await lambdaService.chatWithData(
        selectedFile,
        messageToSend,
        chatMessages
      );

      if (result.success && result.response) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: result.response,
        };
        setChatMessages([...newMessages, assistantMessage]);

        // Update suggested questions if provided
        if (result.suggested_questions && result.suggested_questions.length > 0) {
          setSuggestedQuestions(result.suggested_questions);
        }
      } else {
        toast.error(result.error || 'Failed to get response');
        setChatMessages((prev) => prev.slice(0, -1));
      }
    } catch {
      toast.error('Failed to chat with data');
      setChatMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Handle clearing chat
  const handleClearChat = () => {
    setChatMessages([]);
    setSuggestedQuestions([
      "What are the key patterns in this data?",
      "Summarize the main statistics",
      "What columns have missing values?",
      "Show me the top 10 rows",
    ]);
    toast.success('Chat history cleared');
  };

  // Markdown components for chat messages
  const markdownComponents = {
    h1: ({ children }: { children?: React.ReactNode }) => <h1 className="text-xl font-bold mb-2 mt-3">{children}</h1>,
    h2: ({ children }: { children?: React.ReactNode }) => <h2 className="text-lg font-bold mb-2 mt-3">{children}</h2>,
    h3: ({ children }: { children?: React.ReactNode }) => <h3 className="text-base font-semibold mb-1 mt-2">{children}</h3>,
    p: ({ children }: { children?: React.ReactNode }) => <p className="mb-2">{children}</p>,
    ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc ml-4 mb-2 space-y-1">{children}</ul>,
    ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal ml-4 mb-2 space-y-1">{children}</ol>,
    li: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
    code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
      const isBlock = className?.includes('language-');
      if (isBlock) {
        return (
          <pre className="bg-muted/50 rounded-md p-3 my-2 overflow-x-auto font-mono text-xs">
            <code>{children}</code>
          </pre>
        );
      }
      return <code className="bg-muted/50 px-1 py-0.5 rounded text-xs font-mono">{children}</code>;
    },
    pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="border-l-4 border-primary/50 pl-3 my-2 italic text-muted-foreground">{children}</blockquote>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="overflow-x-auto my-2">
        <table className="min-w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th className="border border-border px-2 py-1 bg-muted/50 text-left font-medium">{children}</th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td className="border border-border px-2 py-1">{children}</td>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a href={href} className="text-primary underline" target="_blank" rel="noopener noreferrer">{children}</a>
    ),
  };

  // Memoized filtered files to prevent recalculation on every render
  const filteredFiles = useMemo(() => {
    if (fileSearchTerm === '') return files;
    return files.filter(file => 
      getDisplayName(file.fileName).toLowerCase().includes(fileSearchTerm.toLowerCase())
    );
  }, [files, fileSearchTerm]);

  useEffect(() => {
    loadFiles();
  }, []);

  useEffect(() => {
    // Check if a file is specified in URL params
    const fileParam = searchParams.get('file');
    if (fileParam && fileParam !== selectedFile) {
      setSelectedFile(fileParam);
      // Only set default query if no query exists yet
      if (!query.trim()) {
        setQuery(`df.lazy().head(10).collect()`);
      }
    }
  }, [searchParams, selectedFile, query]);

  const loadFiles = async () => {
    try {
      const response = await lambdaService.listFiles();
      if (response.success) {
        setFiles(response.files);
        if (response.error) {
          // Show user-friendly message for demo mode
          toast.info(response.error);
        }
      } else {
        // Silent error handling
        toast.error(response.error || 'Unable to load files');
      }
    } catch (error) {
      // Silent error handling
      toast.error('Unable to load files at this time');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // Helper function to fetch and aggregate data from presigned URLs (keeps row groups separate for pagination)
  const fetchFromPresignedUrls = async (presignedUrls: any[]) => {
    try {
      const allResults: any[] = [];
      const rowGroupData: {rowGroupIndex: number, data: any[]}[] = [];
      let totalRowsScanned = 0;
      
      // Fetch data from each presigned URL
      for (const urlInfo of presignedUrls) {
        try {
          const response = await fetch(urlInfo.presigned_url);
          if (!response.ok) continue;
          
          const data = await response.json();
          
          // Extract analysis results
          if (data.analysis_results) {
            // This is column-based data, need to convert to row-based
            const columns = Object.keys(data.analysis_results);
            if (columns.length > 0) {
              const firstColumn = data.analysis_results[columns[0]];
              const rowCount = Array.isArray(firstColumn) ? firstColumn.length : 0;
              
              const rowGroupRows: any[] = [];
              for (let i = 0; i < rowCount; i++) {
                const row: any = {};
                columns.forEach(col => {
                  const values = data.analysis_results[col];
                  row[col] = Array.isArray(values) ? values[i] : values;
                });
                allResults.push(row);
                rowGroupRows.push(row);
              }
              
              // Store this row group's data separately
              rowGroupData.push({
                rowGroupIndex: urlInfo.row_group_index,
                data: rowGroupRows
              });
              
              // Track total rows scanned (not matching rows)
              if (data.file_details?.row_count) {
                totalRowsScanned += data.file_details.row_count;
              }
            }
          }
        } catch (error) {
          // console.error(`Failed to fetch from presigned URL:`, error);
        }
      }
      
      // Sort row groups by index
      rowGroupData.sort((a, b) => a.rowGroupIndex - b.rowGroupIndex);
      
      // Return matching rows count, total rows scanned, and separated row group data
      return { 
        results: allResults, 
        totalRows: allResults.length,  // Matching rows
        totalRowsScanned,  // Total rows scanned across all row groups
        rowGroupData  // Separated data by row group for pagination
      };
    } catch (error) {
      // console.error('Error fetching from presigned URLs:', error);
      return { results: [], totalRows: 0, totalRowsScanned: 0, rowGroupData: [] };
    }
  };

  const runAnalysis = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query');
      return;
    }

    // Clear all previous results immediately to prevent glitching
    setAnalysisResult(null);
    setRowGroupResults([]);
    setCurrentPage(0);
    setCurrentQueryFolder(null);
    
    // Stop any ongoing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setIsPolling(false);

    setIsLoading(true);
    const startTime = Date.now();
    let success = false;
    let resultCount = 0;
    let errorMessage: string | undefined;

    try {
      // For search queries, limit to 1000 rows
      const result = await lambdaService.runAnalysis(selectedFile || '', query, maxParallelism, queryScope, processingLevel, 1000, 1000);
      // console.log('🎯 Analysis result received:', result);
      // console.log('🎯 Has chunkInfo?', !!result?.chunkInfo);
      // console.log('🎯 ChunkInfo details:', result?.chunkInfo);
      
      if (result) {
        success = true;
        resultCount = result.result.length;
        
        // Check if this is an async result with query_folder
        if (result.chunkInfo && result.chunkInfo.metadata && (result.chunkInfo.metadata as any).query_folder) {
          const queryFolder = (result.chunkInfo.metadata as any).query_folder;
          // console.log('✅ Query folder found:', queryFolder);
          setCurrentQueryFolder(queryFolder);
          
          // Show initial async result
          setAnalysisResult(result);
          // console.log('✅ Set analysis result with chunkInfo:', result.chunkInfo);
          
          // Start polling for results
          setIsPolling(true);
          // console.log('🔄 Starting polling...');
          const interval = setInterval(async () => {
            try {
              const pollResult = await lambdaService.getQueryResults(queryFolder, 'list');
              if (pollResult.success) {
                const completedResults = pollResult.query_info?.completed_row_groups || 0;
                const totalRowGroups = pollResult.query_info?.metadata?.total_row_groups || result.chunkInfo?.totalChunks || 0;
                
                // console.log(`📊 Polling: ${completedResults}/${totalRowGroups} completed`);
                
                // Fetch incremental results using presigned URLs
                try {
                  const aggregatedResult = await lambdaService.getQueryResults(queryFolder, 'aggregate');
                  
                  if (aggregatedResult.success && aggregatedResult.presigned_urls && aggregatedResult.presigned_urls.length > 0) {
                    // console.log(`📥 Fetching data from ${aggregatedResult.presigned_urls.length} presigned URLs...`);
                    
                    // Fetch actual data from presigned URLs
                    const { results: transformedResults, totalRows, totalRowsScanned, rowGroupData } = await fetchFromPresignedUrls(aggregatedResult.presigned_urls);
                    
                    // console.log(`✅ Showing ${transformedResults.length} rows (${completedResults}/${totalRowGroups})`);
                    
                    // Store row group data for pagination
                    setRowGroupResults(rowGroupData);
                    
                    const finalResult = {
                      query: query,
                      result: transformedResults,
                      executionTime: Date.now() - startTime,
                      rowCount: totalRows || transformedResults.length,
                      totalRowsScanned: totalRowsScanned,
                      chunkInfo: {
                        ...result.chunkInfo!,
                        successfulChunks: completedResults,
                        failedChunks: pollResult.query_info?.failed_row_groups || 0,
                        totalRows: totalRows || transformedResults.length,
                        totalColumns: transformedResults.length > 0 ? Object.keys(transformedResults[0]).length : 0
                      }
                    };
                    
                    setAnalysisResult(finalResult);
                    
                    // Stop polling when complete
                    if (completedResults >= totalRowGroups && totalRowGroups > 0) {
                      // console.log('✅ Processing complete!');
                      clearInterval(interval);
                      setPollingInterval(null);
                      setIsPolling(false);
                      toast.success(`Analysis completed! Loaded ${totalRows?.toLocaleString()} rows.`);
                    }
                  }
                } catch (aggError) {
                  // console.error('Failed to get results:', aggError);
                }
              }
            } catch (pollError) {
              // console.error('Polling error:', pollError);
            }
          }, 3000); // Poll every 3 seconds
          
          setPollingInterval(interval);
          toast.success(`Analysis started! Results will appear as they become available.`);
        } else {
          // Synchronous result - check if we need to fetch from S3
          if (shouldFetchFromS3(result)) {
            if (!result.s3Results?.display_url) {
              toast.error('Results require S3 but no URL available');
              setAnalysisResult(result);
            } else {
              const rowCount = result.s3Results.full_row_count || result.totalRowsAvailable || 0;
              toast.info(`Fetching ${rowCount.toLocaleString()} rows from S3...`);

              try {
                const fullResults = await fetchAndDecompressS3(result.s3Results.display_url);

                // Update result with full data from S3
                const fullResult = {
                  ...result,
                  result: fullResults,
                  rowCount: fullResults.length,
                  totalRowsAvailable: fullResults.length
                };

                setAnalysisResult(fullResult);
                toast.success(`Loaded all ${fullResults.length.toLocaleString()} rows from S3`);
              } catch (s3Error) {
                console.error('Failed to fetch from S3:', s3Error);
                // Fall back to inline results if available
                if (result.result && result.result.length > 0) {
                  toast.error('Failed to fetch full results from S3, showing sample');
                  setAnalysisResult(result);
                } else {
                  toast.error('Failed to load data from S3');
                }
              }
            }
          } else {
            // Use inline results
            setAnalysisResult(result);

            if (result.result[0]?.status === 'demo_mode') {
              toast.info('Analysis feature is being configured');
            } else {
              toast.success('Analysis completed successfully');
            }
          }
        }
      } else {
        errorMessage = 'Analysis temporarily unavailable';
        toast.error(errorMessage);
      }
    } catch (error) {
      errorMessage = `Analysis error: ${error}`;
      toast.error('Analysis temporarily unavailable');
    } finally {
      const executionTime = Date.now() - startTime;
      setIsLoading(false);

      // Save to query history (fire and forget)
      lambdaService.saveQueryHistory({
        query_type: 'user',
        query_text: query,
        file_name: selectedFile || 'unknown',
        execution_time: executionTime,
        success,
        result_count: resultCount,
        error_message: errorMessage
      }); // .catch(err => console.warn('Failed to save query history:', err));

      // Trigger query history refresh after 5 seconds
      setQueryHistoryRefreshTrigger(Date.now());
    }
  };

  const refreshResults = async () => {
    // Try to get query folder from current state or analysis result metadata
    let queryFolder = currentQueryFolder;
    // console.log('🔍 refreshResults - currentQueryFolder:', currentQueryFolder);
    // console.log('🔍 refreshResults - analysisResult:', analysisResult);
    
    if (!queryFolder && analysisResult?.chunkInfo?.metadata) {
      queryFolder = (analysisResult.chunkInfo.metadata as any).query_folder;
      // console.log('🔍 refreshResults - Got query folder from metadata:', queryFolder);
    }
    
    // If still no query folder, try to extract from folderPath
    if (!queryFolder && analysisResult?.chunkInfo?.folderPath) {
      const folderPath = analysisResult.chunkInfo.folderPath;
      // console.log('🔍 refreshResults - Checking folderPath:', folderPath);
      
      // Check if folderPath looks like a query folder path
      if (folderPath.includes('/queries/rg/')) {
        queryFolder = folderPath;
        // console.log('🔍 refreshResults - Extracted query folder from folderPath:', queryFolder);
      }
    }
    
    if (!queryFolder) {
      // console.error('❌ No query folder found');
      toast.error('No active query to refresh. Please run a query first.');
      return;
    }
    
    // console.log('✅ Refreshing results from query folder:', queryFolder);
    
    try {
      const result = await lambdaService.getQueryResults(queryFolder, 'aggregate');
      // console.log('📊 Aggregation result:', result);
      
      if (result.success && result.presigned_urls && result.presigned_urls.length > 0) {
        const queryInfo = result.query_info;
        
        // console.log(`📥 Fetching data from ${result.presigned_urls.length} presigned URLs...`);
        
        // Fetch actual data from presigned URLs
        const { results: transformedResults, totalRows, totalRowsScanned, rowGroupData } = await fetchFromPresignedUrls(result.presigned_urls);
        
        // Store row group data for pagination
        setRowGroupResults(rowGroupData);
        
        // Convert to AnalysisResult format
        const newAnalysisResult = {
          query: query,
          result: transformedResults,
          executionTime: 0,
          rowCount: totalRows || transformedResults.length,
          totalRowsScanned: totalRowsScanned,
          chunkInfo: {
            totalChunks: queryInfo?.metadata?.total_row_groups || 0,
            successfulChunks: queryInfo?.completed_row_groups || 0,
            failedChunks: queryInfo?.failed_row_groups || 0,
            totalRows: totalRows || transformedResults.length,
            totalColumns: transformedResults.length > 0 ? Object.keys(transformedResults[0]).length : 0,
            folderPath: queryFolder,
            processingType: 'row_group_parallel' as const,
            metadata: {
              ...queryInfo?.metadata,
              query_folder: queryFolder
            }
          }
        };
        
        setAnalysisResult(newAnalysisResult);
        setCurrentQueryFolder(queryFolder); // Ensure it's set for next refresh
        toast.success(`Refreshed! Found ${totalRows?.toLocaleString()} rows with ${newAnalysisResult.chunkInfo.totalColumns} columns.`);
      } else {
        toast.error('No results available yet. Analysis may still be processing.');
      }
    } catch (error) {
      // console.error('Refresh failed:', error);
      toast.error('Failed to refresh results');
    }
  };

  const runSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    // Clear all previous results immediately to prevent glitching
    setAnalysisResult(null);
    setRowGroupResults([]);
    setCurrentPage(0);
    setCurrentQueryFolder(null);
    
    // Stop any ongoing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setIsPolling(false);

    setIsLoading(true);
    setExecutedSearchTerm(searchTerm); // Store the executed search term
    setHasShownCompletionToast(false); // Reset completion toast flag for new search
    const startTime = Date.now();
    let success = false;
    let resultCount = 0;
    let errorMessage: string | undefined;

    try {
      // Create a Polars query that searches across all columns
      // The query MUST assign to 'result' variable for the backend
      const searchQuery = `# Search across all columns for the term: "${searchTerm}" (case-insensitive)
import polars as pl

# Get the search term
search_term_lower = "${searchTerm.toLowerCase()}"

# Get schema to identify searchable columns
schema = df.collect_schema()

# Build search conditions only for columns that can be searched
search_conditions = []
for col_name in schema.names():
    col_type = schema[col_name]
    # Only search string columns and numeric columns (which can be cast to string)
    if col_type in [pl.Utf8, pl.String, pl.Int8, pl.Int16, pl.Int32, pl.Int64, pl.UInt8, pl.UInt16, pl.UInt32, pl.UInt64, pl.Float32, pl.Float64, pl.Boolean]:
        search_conditions.append(
            pl.col(col_name).cast(pl.Utf8).fill_null("").str.to_lowercase().str.contains(search_term_lower, literal=True)
        )

# Filter rows where any searchable column contains the search term
if search_conditions:
    result = df.lazy().filter(
        pl.any_horizontal(search_conditions)
    ).collect()
else:
    # No searchable columns found
    result = df.lazy().limit(0).collect()`.trim();

      const result = await lambdaService.runAnalysis(selectedFile || '', searchQuery, maxParallelism, queryScope, processingLevel);
      // console.log('🔍 Search result:', result);
      if (result) {
        success = true;
        // Get result count from the transformed result array
        resultCount = result.result?.length || 0;
        
        // Handle async processing for search
        if (result.chunkInfo && result.chunkInfo.metadata?.query_folder) {
          const queryFolder = result.chunkInfo.metadata.query_folder;
          setCurrentQueryFolder(queryFolder);
          setAnalysisResult(result);
          
          // Start polling for search results
          setIsPolling(true);
          const interval = setInterval(async () => {
            try {
              const pollResult = await lambdaService.getQueryResults(queryFolder, 'list');
              if (pollResult.success) {
                const completedResults = pollResult.query_info?.completed_row_groups || 0;
                const totalRowGroups = pollResult.query_info?.metadata?.total_row_groups || result.chunkInfo?.totalChunks || 0;
                
                // console.log(`📊 Search polling: ${completedResults}/${totalRowGroups} completed`);
                
                // Fetch incremental search results using presigned URLs
                try {
                  const aggregatedResult = await lambdaService.getQueryResults(queryFolder, 'aggregate');
                  if (aggregatedResult.success && aggregatedResult.presigned_urls && aggregatedResult.presigned_urls.length > 0) {
                    // console.log(`📥 Fetching search data from ${aggregatedResult.presigned_urls.length} presigned URLs...`);
                    
                    // Fetch actual data from presigned URLs
                    const { results: transformedResults, totalRows, totalRowsScanned, rowGroupData } = await fetchFromPresignedUrls(aggregatedResult.presigned_urls);
                    
                    const matchingRows = transformedResults.length; // Actual matching rows
                    
                    // Store row group data for pagination
                    setRowGroupResults(rowGroupData);
                    
                    const finalResult = {
                      query: searchQuery,
                      result: transformedResults,
                      executionTime: Date.now() - startTime,
                      rowCount: matchingRows,
                      totalRowsScanned: totalRowsScanned, // Store total rows scanned for display
                      chunkInfo: {
                        ...result.chunkInfo!,
                        successfulChunks: completedResults,
                        failedChunks: pollResult.query_info?.failed_row_groups || 0,
                        totalRows: matchingRows,
                        totalColumns: transformedResults.length > 0 ? Object.keys(transformedResults[0]).length : 0
                      }
                    };
                    
                    setAnalysisResult(finalResult);
                    
                    // Stop polling when complete
                    if (completedResults >= totalRowGroups && totalRowGroups > 0) {
                      clearInterval(interval);
                      setPollingInterval(null);
                      setIsPolling(false);
                      // Only show toast if we haven't already shown one
                      if (!hasShownCompletionToast) {
                        toast.success(`Search completed! Found ${matchingRows.toLocaleString()} matching rows.`);
                        setHasShownCompletionToast(true);
                      }
                    }
                  }
                } catch (aggError) {
                  // console.error('Failed to get search results:', aggError);
                }
              }
            } catch (pollError) {
              // console.error('Search polling error:', pollError);
            }
          }, 3000);
          
          setPollingInterval(interval);
          toast.success(`Search started! Results will appear as they become available.`);
        } else {
          // Synchronous search result
          setAnalysisResult(result);
          if (result.result[0]?.status === 'demo_mode') {
            toast.info('Search feature is being configured');
          } else {
            toast.success(`Search completed! Found ${resultCount} matching rows.`);
          }
        }
      } else {
        errorMessage = 'Search temporarily unavailable';
        toast.error(errorMessage);
      }
    } catch (error) {
      errorMessage = `Search error: ${error}`;
      toast.error('Search temporarily unavailable');
    } finally {
      const executionTime = Date.now() - startTime;
      setIsLoading(false);

      // Save to query history (fire and forget)
      lambdaService.saveQueryHistory({
        query_type: 'user',
        query_text: `Search: "${searchTerm}"`,
        file_name: selectedFile || 'unknown',
        execution_time: executionTime,
        success,
        result_count: resultCount,
        error_message: errorMessage
      }); // .catch(err => console.warn('Failed to save search history:', err));

      // Trigger query history refresh
      setQueryHistoryRefreshTrigger(Date.now());
    }
  };

  const exportResults = async () => {
    if (!analysisResult || !analysisResult.result.length) {
      toast.error('No results to export');
      return;
    }

    // Re-run query with higher result limit (10000 rows) for export
    toast.info('Preparing export with up to 10,000 rows...');

    try {
      const exportResult = await lambdaService.runAnalysis(selectedFile || '', query, maxParallelism, queryScope, processingLevel, 1000, 10000);

      if (exportResult?.s3Results?.download_url) {
        // console.log('📥 Using S3 download URL for export (10K rows)');
        toast.info(`Downloading ${exportResult.s3Results.full_row_count?.toLocaleString()} rows from S3...`);

        // Open download URL in new tab (will download automatically)
        window.open(exportResult.s3Results.download_url, '_blank');
        toast.success('Download started!');
        return;
      }
    } catch (error) {
      // console.error('Failed to generate export with 10K rows:', error);
      toast.error('Export failed, falling back to current results');
    }

    // Fallback: If S3 download URL is available from current results, use it
    if (analysisResult.s3Results?.download_url) {
      // console.log('📥 Using S3 download URL for export (fallback)');
      toast.info(`Downloading ${analysisResult.s3Results.full_row_count?.toLocaleString()} rows from S3...`);

      // Open download URL in new tab (will download automatically)
      window.open(analysisResult.s3Results.download_url, '_blank');
      toast.success('Download started!');
      return;
    }

    let dataToExport = analysisResult.result;

    // If results are truncated, fetch all data
    if (analysisResult.resultTruncated && analysisResult.totalRowsAvailable) {
      // Try to get query folder from various sources (for row-group mode)
      let queryFolder = currentQueryFolder;
      if (!queryFolder && analysisResult?.chunkInfo?.metadata) {
        queryFolder = (analysisResult.chunkInfo.metadata as any).query_folder;
      }
      if (!queryFolder && analysisResult?.chunkInfo?.folderPath) {
        const folderPath = analysisResult.chunkInfo.folderPath;
        if (folderPath.includes('/queries/')) {
          queryFolder = folderPath;
        }
      }

      if (queryFolder) {
        // Row-group mode: fetch from query folder using presigned URLs
        try {
          toast.info(`Fetching all ${analysisResult.totalRowsAvailable.toLocaleString()} rows from query results...`);

          // Fetch presigned URLs for all result files
          const fullResult = await lambdaService.getQueryResults(queryFolder, 'aggregate');

          if (fullResult && fullResult.presigned_urls && fullResult.presigned_urls.length > 0) {
            // Fetch data from all presigned URLs
            const allData: any[] = [];

            for (let i = 0; i < fullResult.presigned_urls.length; i++) {
              const urlInfo = fullResult.presigned_urls[i];
              try {
                const response = await fetch(urlInfo.presigned_url);
                const data = await response.json();

                // The data might be in different formats depending on what was stored
                if (Array.isArray(data)) {
                  allData.push(...data);
                } else if (data.result && Array.isArray(data.result)) {
                  allData.push(...data.result);
                } else if (data.data && Array.isArray(data.data)) {
                  allData.push(...data.data);
                }
              } catch (error) {
                // console.error(`Failed to fetch data from presigned URL ${i}:`, error);
              }
            }

            if (allData.length > 0) {
              dataToExport = allData;
              toast.success(`Fetched all ${dataToExport.length.toLocaleString()} rows`);
            } else {
              toast.error('Failed to fetch all results. Exporting displayed results only.');
            }
          } else {
            toast.error('No presigned URLs available. Exporting displayed results only.');
          }
        } catch (error) {
          // console.error('Failed to fetch all results from query folder:', error);
          toast.error('Failed to fetch all results. Exporting displayed results only.');
        }
      } else {
        // All Groups mode: Fetch all results with no limit
        try {
          toast.info(`Fetching all ${analysisResult.totalRowsAvailable.toLocaleString()} rows. This may take a moment...`);
          // console.log('🔍 Export: Fetching all rows with limit:', analysisResult.totalRowsAvailable);

          // Re-run with same mode but request all rows
          const fullResult = await lambdaService.runAnalysis(
            selectedFile,
            analysisMode === 'search' ? analysisResult.query : query,
            maxParallelism,
            queryScope,
            processingLevel, // Keep using 'file' mode
            analysisResult.totalRowsAvailable // Request all rows
          );

          // console.log('🔍 Export: Full result received:', fullResult);

          if (fullResult && fullResult.result && fullResult.result.length > 0) {
            dataToExport = fullResult.result;
            toast.success(`Fetched all ${dataToExport.length.toLocaleString()} rows`);
          } else {
            toast.error('Failed to fetch all results. Exporting displayed results only.');
          }
        } catch (error) {
          // console.error('Failed to fetch all results:', error);
          toast.error('Export failed. The dataset may be too large for synchronous export.');
        }
      }
    }

    const headers = Object.keys(dataToExport[0]);
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row =>
        headers.map(header =>
          typeof row[header] === 'string' && row[header].includes(',')
            ? `"${row[header]}"`
            : row[header]
        ).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analysis_results.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${dataToExport.length.toLocaleString()} rows to CSV`);
  };

  const handleVisualize = () => {
    if (!analysisResult || !analysisResult.result.length) {
      toast.error('No results to visualize');
      return;
    }

    // Limit to 10,000 rows for visualization performance
    const dataToVisualize = analysisResult.result.slice(0, 10000);

    // Store data in sessionStorage for the visualization page
    sessionStorage.setItem('bigsplice-visualize-data', JSON.stringify({
      data: dataToVisualize,
      fileName: selectedFile,
      query: query,
      timestamp: Date.now(),
    }));

    // Navigate to visualization page
    navigate('/visualize?source=analyze');
    toast.info(`Visualizing ${dataToVisualize.length.toLocaleString()} rows`);
  };

  const redoQuery = async () => {
    if (!lastUsedPrompt || !lastGeneratedQuery) {
      toast.error('No previous query to redo. Generate a query first.');
      return;
    }

    // Create the redo prompt
    const redoPrompt = `${lastUsedPrompt}

This is the query you gave me last time, but it did not work, revise it:
\`\`\`
${lastGeneratedQuery}
\`\`\``;

    setNaturalLanguagePrompt(redoPrompt);
    
    // Auto-generate with the redo prompt
    setIsGeneratingQuery(true);
    const startTime = Date.now();

    try {
      // Get sample data from the current analysis result or run a quick query
      let headers: string[] = [];
      let sampleRows: any[][] = [];

      if (analysisResult && analysisResult.result.length > 0) {
        // Use existing results if available
        headers = Object.keys(analysisResult.result[0]);
        sampleRows = analysisResult.result.slice(0, 5).map(row => 
          headers.map(header => row[header])
        );
      } else {
        // Run a quick query to get sample data
        const sampleResult = await lambdaService.runAnalysis(selectedFile, 'df.lazy().head(5).collect()');
        if (sampleResult && sampleResult.result.length > 0) {
          headers = Object.keys(sampleResult.result[0]);
          sampleRows = sampleResult.result.map(row => 
            headers.map(header => row[header])
          );
        }
      }
      
      // Model configuration
      const modelConfig = {
        provider: aiProvider,
        claudeModel: aiProvider === 'claude' ? claudeModel : undefined,
        gpt5ReasoningEffort: aiProvider === 'gpt-5' ? gpt5ReasoningEffort : undefined
      };

      const result = await lambdaService.generateQuery(redoPrompt, headers, sampleRows, selectedFile, modelConfig);

      const executionTime = Date.now() - startTime;

      // Get model info string
      let modelInfo = 'Unknown';
      if (aiProvider === 'gpt-5') {
        modelInfo = `GPT-5 (${gpt5ReasoningEffort} reasoning)`;
      } else if (aiProvider === 'claude') {
        switch (claudeModel) {
          case 'claude-haiku-4-5':
            modelInfo = 'Claude 4.5 Haiku';
            break;
          case 'claude-3-haiku-20240307':
            modelInfo = 'Claude 3 Haiku';
            break;
          case 'claude-3-opus-20240229':
            modelInfo = 'Claude 3 Opus';
            break;
          case 'claude-3-5-sonnet-20241022':
          default:
            modelInfo = 'Claude 3.5 Sonnet';
            break;
        }
      }

      // Save to query history (fire and forget)
      lambdaService.saveQueryHistory({
        query_type: 'ai_generated',
        query_text: result.query || '',
        prompt: redoPrompt,
        file_name: selectedFile || 'unknown',
        model_info: modelInfo,
        execution_time: executionTime,
        success: result.success,
        result_count: 0, // We don't know result count for generated queries
        error_message: result.error
      }); // .catch(err => console.warn('Failed to save AI redo query history:', err));

      // Trigger query history refresh after 5 seconds
      setQueryHistoryRefreshTrigger(Date.now());

      if (result.success && result.query) {
        setQuery(result.query);
        setLastUsedPrompt(lastUsedPrompt); // Keep the original prompt for future redos
        setLastGeneratedQuery(result.query); // Update with the new revised query
        toast.success(`Query revised! ${result.explanation || ''}`);
        // Don't clear the prompt - leave it for user to see what was sent
      } else {
        toast.error(`Failed to revise query: ${result.error}`);
      }
    } catch (error) {
      // console.error('Error revising query:', error);
      toast.error(`Error revising query: ${error}`);
    } finally {
      setIsGeneratingQuery(false);
    }
  };

  const generateQueryFromPrompt = async () => {
    if (!naturalLanguagePrompt.trim()) {
      toast.error('Please enter a description of what you want to analyze');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setIsGeneratingQuery(true);

    try {
      // Get sample data from the current analysis result or run a quick query
      let headers: string[] = [];
      let sampleRows: any[][] = [];

      if (analysisResult && analysisResult.result.length > 0) {
        // Use existing results if available
        headers = Object.keys(analysisResult.result[0]);
        sampleRows = analysisResult.result.slice(0, 5).map(row => 
          headers.map(header => row[header])
        );
      } else {
        // Run a quick query to get sample data
        const sampleResult = await lambdaService.runAnalysis(selectedFile, 'df.lazy().head(5).collect()');
        if (sampleResult && sampleResult.result.length > 0) {
          headers = Object.keys(sampleResult.result[0]);
          sampleRows = sampleResult.result.map(row => 
            headers.map(header => row[header])
          );
        } else {
          toast.error('Could not get sample data from the file');
          return;
        }
      }

      // console.log('Generating query with:', { headers, sampleRows });

      const modelConfig = {
        provider: aiProvider,
        claudeModel,
        gpt5ReasoningEffort
      };
      
      const startTime = Date.now();
      const result = await lambdaService.generateQuery(naturalLanguagePrompt, headers, sampleRows, selectedFile, modelConfig);
      const executionTime = Date.now() - startTime;

      // Get model info string
      let modelInfo = 'Unknown';
      if (aiProvider === 'gpt-5') {
        modelInfo = `GPT-5 (${gpt5ReasoningEffort} reasoning)`;
      } else if (aiProvider === 'claude') {
        switch (claudeModel) {
          case 'claude-haiku-4-5':
            modelInfo = 'Claude 4.5 Haiku';
            break;
          case 'claude-3-haiku-20240307':
            modelInfo = 'Claude 3 Haiku';
            break;
          case 'claude-3-opus-20240229':
            modelInfo = 'Claude 3 Opus';
            break;
          case 'claude-3-5-sonnet-20241022':
          default:
            modelInfo = 'Claude 3.5 Sonnet';
            break;
        }
      }

      // Save to query history (fire and forget)
      lambdaService.saveQueryHistory({
        query_type: 'ai_generated',
        query_text: result.query || '',
        prompt: naturalLanguagePrompt,
        file_name: selectedFile || 'unknown',
        model_info: modelInfo,
        execution_time: executionTime,
        success: result.success,
        result_count: 0, // We don't know result count for generated queries
        error_message: result.error
      }); // .catch(err => console.warn('Failed to save AI query history:', err));

      // Trigger query history refresh after 5 seconds
      setQueryHistoryRefreshTrigger(Date.now());

      if (result.success && result.query) {
        setQuery(result.query);
        setLastUsedPrompt(naturalLanguagePrompt); // Store the prompt before clearing
        setLastGeneratedQuery(result.query); // Store the generated query for redo
        toast.success(`Query generated! ${result.explanation || ''}`);
        setNaturalLanguagePrompt(''); // Clear the prompt
      } else {
        toast.error(`Failed to generate query: ${result.error}`);
      }
    } catch (error) {
      // console.error('Error generating query:', error);
      toast.error(`Error generating query: ${error}`);
    } finally {
      setIsGeneratingQuery(false);
    }
  };

  // Example queries - simple, ready-to-run without editing
  const exampleQueries = [
    {
      title: 'First 10 rows',
      description: 'Preview the beginning of your data',
      query: `result = df.lazy().head(10).collect()`
    },
    {
      title: 'Last 10 rows',
      description: 'Preview the end of your data',
      query: `result = df.lazy().tail(10).collect()`
    },
    {
      title: 'First 100 rows',
      description: 'Load more data for analysis',
      query: `result = df.lazy().head(100).collect()`
    },
    {
      title: 'First 1000 rows',
      description: 'Larger sample for deeper analysis',
      query: `result = df.lazy().head(1000).collect()`
    },
    {
      title: 'Count total rows',
      description: 'See how many rows in your dataset',
      query: `result = df.lazy().select(pl.count().alias("total_rows")).collect()`
    },
    {
      title: 'Column statistics',
      description: 'Get min, max, mean for all columns',
      query: `result = df.lazy().select(pl.all().min().name.suffix("_min"), pl.all().max().name.suffix("_max")).head(1).collect()`
    }
  ];


  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      <TopNav />
      <div className="flex flex-col" style={{ height: 'calc(100vh - 4.75rem)' }}>
        <div className="px-6 py-3 border-b border-border shrink-0">
          <h1 className="text-base font-semibold text-foreground">Data Analysis</h1>
          <p className="text-sm text-muted-foreground">
            Use Dataframe queries to analyze your data
          </p>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* File List - Left Sidebar */}
          <div className="hidden w-[15%] border-r border-border flex-shrink-0 flex flex-col">
            <div className="p-4 border-b border-border">
              <h2 className="text-sm font-semibold">Available Files</h2>
              <p className="text-xs text-muted-foreground mb-3">
                Click to select a file for analysis
              </p>
              {/* File Search Box */}
              <Input
                placeholder="Search files..."
                value={fileSearchTerm}
                onChange={handleFileSearchChange}
                className="text-xs h-8"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
                {isLoadingFiles ? (
                  <div className="py-4 space-y-2">
                    <div className="text-sm text-muted-foreground text-center">Loading files...</div>
                    <Progress className="w-full" />
                  </div>
                ) : files.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <FileText className="h-8 w-8 mx-auto mb-2" />
                    <p>No files uploaded yet</p>
                    <Button asChild className="mt-2" size="sm">
                      <Link to="/upload">Upload Files</Link>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredFiles.map((file) => (
                      <div
                        key={file.s3Key || file.fileName}
                        onClick={() => {
                          // Use s3Key for analysis if available (for multipart datasets with folder paths)
                          // Otherwise fall back to fileName
                          setSelectedFile(file.s3Key || file.fileName);
                          setQuery(`df.lazy().head(10).collect()`);
                        }}
                        className={`p-2 border cursor-pointer transition-colors ${
                          (selectedFile === file.fileName || selectedFile === file.s3Key)
                            ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/30'
                            : 'border-border hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{getDisplayName(file.fileName)}</p>
                            <p className="text-xs text-gray-500">
                              {file.sizeMB ? file.sizeMB.toFixed(2) : (file.fileSize / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                          {file.isEnhanced && (
                            <div className="ml-2 flex-shrink-0">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                Multi-GPU
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>

          {/* Analysis Section */}
          <div className="flex-1 border-r border-border overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Tabs value={analysisMode} onValueChange={(value) => setAnalysisMode(value as 'query' | 'search')}>
                    <TabsList className="grid w-fit grid-cols-2">
                      <TabsTrigger value="query" className="flex items-center">
                        <Code className="h-4 w-4 mr-2" />
                        Query
                      </TabsTrigger>
                      <TabsTrigger value="search" className="flex items-center">
                        <Search className="h-4 w-4 mr-2" />
                        Search
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <p className="text-xs text-muted-foreground mt-2">
                    {analysisMode === 'query' 
                      ? 'Write standard Dataframe queries to analyze your data'
                      : 'Search across all columns in your dataset'
                    }
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <Tabs value={analysisMode} onValueChange={(value) => setAnalysisMode(value as 'query' | 'search')}>
                <div className="space-y-4">
                  {/* File indicator */}
                  {selectedFile ? (
                    <div className="p-2 bg-primary/10 border border-primary/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Database className="h-4 w-4 text-primary" />
                          <div>
                            <p className="text-sm font-medium text-primary">
                              {getDisplayName(selectedFile)}
                            </p>
                            <p className="text-xs text-primary/70">
                              Selected file for analysis
                            </p>
                          </div>
                        </div>
                        {(() => {
                          const fileInfo = files.find(f => f.fileName === selectedFile);
                          const isLargeFile = fileInfo && fileInfo.fileSize > 100 * 1024 * 1024;
                          
                          return isLargeFile ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              <BarChart3 className="h-3 w-3 mr-1" />
                              Multi-GPU
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-muted border border-border rounded-lg">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select a file to analyze
                      </p>
                      <SearchableFileSelect
                        files={files}
                        selectedFile={selectedFile}
                        onFileSelect={(fileKey) => {
                          setSelectedFile(fileKey);
                          if (!query.trim()) {
                            setQuery(`df.lazy().head(10).collect()`);
                          }
                        }}
                        isLoading={isLoadingFiles}
                        placeholder="Search and select a file..."
                      />
                    </div>
                  )}

                  <TabsContent value="query" className="space-y-4 mt-0">{/* Query Mode Content */}

                  {/* Talk to Your Data - Chat Interface */}
                    <Card className="border-primary/20 mt-4">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="flex items-center gap-2 text-base">
                              <MessageSquare className="h-5 w-5 text-primary" />
                              Talk to Your Data
                            </CardTitle>
                            <CardDescription className="mt-1 text-xs">
                              Ask questions about your data in natural language
                            </CardDescription>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                AWS Bedrock (GPT-OSS-120B)
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Your data stays within AWS
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {chatMessages.length > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleClearChat}
                                className="text-muted-foreground hover:text-destructive hover:border-destructive h-7 text-xs"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3 pt-0">
                        {/* Chat Messages */}
                        <div className="bg-muted/30 rounded-lg p-3 h-[250px] overflow-y-auto space-y-3">
                          {chatMessages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                              <Bot className="h-10 w-10 mb-3 opacity-50" />
                              <p className="text-sm font-medium mb-1">Start a conversation</p>
                              <p className="text-xs max-w-md">
                                Ask me anything about your data - patterns, statistics, or insights.
                              </p>
                            </div>
                          ) : (
                            chatMessages.map((msg, i) => (
                              <div
                                key={i}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-[85%] rounded-lg px-3 py-2 ${
                                    msg.role === 'user'
                                      ? 'bg-primary text-primary-foreground'
                                      : 'bg-card border'
                                  }`}
                                >
                                  {msg.role === 'assistant' && (
                                    <div className="flex items-center gap-1 mb-1 text-xs text-muted-foreground">
                                      <Bot className="h-3 w-3" />
                                      AI
                                    </div>
                                  )}
                                  <div className={`text-sm ${msg.role === 'user' ? '' : 'max-w-none'}`}>
                                    {msg.role === 'user' ? (
                                      msg.content
                                    ) : (
                                      <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={markdownComponents}
                                      >
                                        {msg.content}
                                      </ReactMarkdown>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                          {isSendingMessage && (
                            <div className="flex justify-start">
                              <div className="bg-card border rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  <span className="text-xs">Analyzing...</span>
                                </div>
                              </div>
                            </div>
                          )}
                          <div ref={chatEndRef} />
                        </div>

                        {/* Suggested Questions */}
                        {suggestedQuestions.length > 0 && chatMessages.length < 4 && (
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground font-medium">Suggestions:</p>
                            <div className="flex flex-wrap gap-1">
                              {suggestedQuestions.slice(0, 4).map((question, i) => (
                                <Button
                                  key={i}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-auto py-1 px-2"
                                  onClick={() => handleSendMessage(question)}
                                  disabled={isSendingMessage}
                                >
                                  {question}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Chat Input */}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Ask a question about your data..."
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                              }
                            }}
                            disabled={isSendingMessage}
                            className="flex-1 h-9 text-sm"
                          />
                          <Button
                            onClick={() => handleSendMessage()}
                            disabled={isSendingMessage || !chatInput.trim()}
                            size="sm"
                            className="h-9"
                          >
                            {isSendingMessage ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                  {/* Advanced: AI Query Generator - Collapsible */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/30 mt-4">
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer"
                      onClick={() => setAdvancedQueryCollapsed(!advancedQueryCollapsed)}
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Advanced: Query Generator
                        </span>
                      </div>
                      {advancedQueryCollapsed ? (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </div>

                    {!advancedQueryCollapsed && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        {/* AI Query Generator */}
                        <div className="border-x-0 border-t-0 border-b-0 border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-4">
                    <label className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3 block flex items-center">
                      <Sparkles className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                      AI Query Generator
                    </label>
                    <div className="flex space-x-2">
                      <Textarea
                        value={naturalLanguagePrompt}
                        onChange={handleNaturalLanguagePromptChange}
                        placeholder="Describe what you want to analyze (e.g., 'Show me the average salary by city')"
                        className="flex-1 min-h-[60px] text-sm bg-white dark:bg-gray-900"
                      />
                      <Button 
                        onClick={generateQueryFromPrompt}
                        disabled={isGeneratingQuery || !selectedFile}
                        className="self-end bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white"
                      >
                        {isGeneratingQuery ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* AI Model Selection */}
                    <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-700">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setAiConfigCollapsed(!aiConfigCollapsed)}
                      >
                        <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                          AI Model Configuration
                        </Label>
                        <div className="flex items-center space-x-2">
                          {aiConfigCollapsed && (
                            <span className="text-xs text-muted-foreground">
                              {getAiConfigSummary()}
                            </span>
                          )}
                          {aiConfigCollapsed ? (
                            <ChevronRight className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                        </div>
                      </div>
                      
                      {!aiConfigCollapsed && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                        {/* Provider Selection */}
                        <div>
                          <Label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Provider</Label>
                          <Select value={aiProvider} onValueChange={(value: 'gpt-5' | 'claude' | 'bedrock') => setAiProvider(value)}>
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gpt-5">GPT-5</SelectItem>
                              <SelectItem value="claude">Claude</SelectItem>
                              <SelectItem value="bedrock">Secure GPT // AWS Bedrock</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Claude Model Selection */}
                        {aiProvider === 'claude' && (
                          <div>
                            <Label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Claude Model</Label>
                            <Select value={claudeModel} onValueChange={(value: 'claude-3-5-sonnet-20241022' | 'claude-3-haiku-20240307' | 'claude-3-opus-20240229' | 'claude-haiku-4-5') => setClaudeModel(value)}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="claude-haiku-4-5">Claude 4.5 Haiku (Fastest & Cheapest)</SelectItem>
                                <SelectItem value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Balanced)</SelectItem>
                                <SelectItem value="claude-3-haiku-20240307">Claude 3 Haiku (Fast)</SelectItem>
                                <SelectItem value="claude-3-opus-20240229">Claude 3 Opus (Premium)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Bedrock Model Selection */}
                        {aiProvider === 'bedrock' && (
                          <div>
                            <Label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Bedrock Model</Label>
                            <Select value={bedrockModel} onValueChange={(value: 'openai.gpt-oss-120b-1:0' | 'openai.gpt-oss-20b-1:0') => setBedrockModel(value)}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="openai.gpt-oss-120b-1:0">GPT-OSS-120B</SelectItem>
                                <SelectItem value="openai.gpt-oss-20b-1:0">GPT-OSS-20B</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* GPT-5 Reasoning Effort */}
                        {aiProvider === 'gpt-5' && (
                          <div>
                            <Label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Reasoning Effort</Label>
                            <Select value={gpt5ReasoningEffort} onValueChange={(value: 'minimal' | 'low' | 'medium' | 'high') => setGpt5ReasoningEffort(value)}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="minimal">Minimal (Fastest)</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High (Most Thorough)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-gray-500">
                            {aiProvider === 'gpt-5'
                              ? `Powered by GPT-5 (${gpt5ReasoningEffort} reasoning) • Generates lazy Dataframe queries from natural language`
                              : aiProvider === 'bedrock'
                              ? (
                                <span>
                                  Powered by{' '}
                                  <a
                                    href="https://aws.amazon.com/bedrock/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                                  >
                                    AWS Bedrock
                                  </a>
                                  {' '}({bedrockModel === 'openai.gpt-oss-20b-1:0' ? 'GPT-OSS-20B' : 'GPT-OSS-120B'})
                                </span>
                              )
                              : `Powered by ${claudeModel === 'claude-haiku-4-5' ? 'Claude 4.5 Haiku' :
                                   claudeModel === 'claude-3-5-sonnet-20241022' ? 'Claude 3.5 Sonnet' :
                                   claudeModel === 'claude-3-haiku-20240307' ? 'Claude 3 Haiku' : 'Claude 3 Opus'} • Generates lazy Dataframe queries from natural language`
                            }
                          </p>
                          {aiProvider === 'bedrock' && (
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Your data stays within AWS and is never sent to any 3rd party service
                            </p>
                          )}
                        </div>
                        </div>
                      )}
                    </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Advanced Settings with GPU Threads and Query Scope */}
                  {selectedFile && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/30 rounded border border-gray-200 dark:border-gray-700">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setParallelismConfigCollapsed(!parallelismConfigCollapsed)}
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
                            Advanced Settings
                          </Label>
                          {parallelismConfigCollapsed && (
                            <span className="text-xs text-muted-foreground">
                              {maxParallelism} GPU threads{files.find(f => f.fileName === selectedFile)?.isMultipart ? `, ${queryScope === 'first' ? 'Sample' : 'Full Dataset'}` : ''}
                            </span>
                          )}
                          {parallelismConfigCollapsed ? (
                            <ChevronRight className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                        </div>
                      </div>

                      {!parallelismConfigCollapsed && (
                        <div className="mt-3 space-y-4">
                          {/* GPU Threads */}
                          <div>
                            <Label className="text-xs text-gray-700 dark:text-gray-300 mb-2 block">
                              GPU Threads: {maxParallelism}
                            </Label>
                            <div className="flex items-center space-x-3">
                              <span className="text-xs text-gray-500">1</span>
                              <Slider
                                value={[maxParallelism]}
                                onValueChange={(value) => setMaxParallelism(value[0])}
                                min={1}
                                max={500}
                                step={1}
                                className="flex-1"
                              />
                              <span className="text-xs text-gray-500">500</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              Controls parallel GPU thread count for processing. Default: 500. Decrease for smaller files or to reduce Lambda concurrency.
                            </p>
                          </div>

                          {/* Query Scope - Show only for large files */}
                          {files.find(f => f.fileName === selectedFile)?.isMultipart && (
                            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                              <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                                Data Scope
                              </Label>
                              <div className="flex gap-3">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name="queryScope"
                                    value="first"
                                    checked={queryScope === 'first'}
                                    onChange={(e) => setQueryScope(e.target.value as 'first' | 'all')}
                                    className="mr-2"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sample</span>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Quick preview (~1%)</p>
                                  </div>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name="queryScope"
                                    value="all"
                                    checked={queryScope === 'all'}
                                    onChange={(e) => setQueryScope(e.target.value as 'first' | 'all')}
                                    className="mr-2"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Full Dataset</span>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">All data</p>
                                  </div>
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}


                  {/* Query textarea */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Dataframe Query
                    </label>
                    <div className="relative">
                      <Textarea
                        value={query}
                        onChange={handleQueryChange}
                        placeholder="Enter your Dataframe query here..."
                        className="mono min-h-[200px] text-sm pr-44"
                      />
                      <div className="absolute bottom-3 right-3 flex space-x-2">
                        {/* Redo Button - show only when we have AI context from a previous generation */}
                        {lastUsedPrompt && lastGeneratedQuery && selectedFile && (
                          <Button
                            onClick={redoQuery}
                            disabled={isGeneratingQuery}
                            variant="outline"
                            className="h-8 px-3 text-xs bg-orange-50 dark:bg-orange-950 border-orange-300 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900"
                            title="Redo: Ask AI to revise the previous query that didn't work"
                          >
                            {isGeneratingQuery ? (
                              <>
                                <Spinner size="sm" className="mr-2" />
                                Redoing...
                              </>
                            ) : (
                              <>
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Redo
                              </>
                            )}
                          </Button>
                        )}

                        {/* Refine Query Button - show if we have any query but no AI context */}
                        {query.trim() && selectedFile && !lastUsedPrompt && (
                          <Button
                            onClick={() => {
                              const refinePrompt = `I have this query that I want to improve or fix:

\`\`\`
${query}
\`\`\`

Please help me refine this query to make it better, more efficient, or fix any issues.`;
                              setNaturalLanguagePrompt(refinePrompt);
                              // Scroll to AI generator section
                              document.querySelector('.border-blue-200')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            disabled={isGeneratingQuery}
                            variant="outline"
                            className="h-8 px-3 text-xs"
                            title="Refine this query using AI assistance"
                          >
                            <Zap className="h-3 w-3 mr-1" />
                            Refine Query
                          </Button>
                        )}
                        
                        {/* Run Query Button */}
                        <Button 
                          onClick={runAnalysis} 
                          disabled={isLoading}
                          className="h-8 px-3 text-xs"
                        >
                          {isLoading ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              Running...
                            </>
                          ) : (
                            <>
                              <Play className="h-3 w-3 mr-1" />
                              Run Query
                            </>
                          )}
                        </Button>
                        
                        {/* Refresh Results Button */}
                        {currentQueryFolder && (
                          <Button 
                            onClick={refreshResults} 
                            disabled={isLoading}
                            variant="outline"
                            className="h-8 px-3 text-xs"
                          >
                            <RefreshCw className={`h-3 w-3 mr-1 ${isPolling ? 'animate-spin' : ''}`} />
                            Refresh
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  </TabsContent>

                  <TabsContent value="search" className="space-y-4 mt-0">
                    {/* Advanced Settings with GPU Threads and Search Scope */}
                    {selectedFile && (
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/30 rounded border border-gray-200 dark:border-gray-700">
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => setParallelismConfigCollapsed(!parallelismConfigCollapsed)}
                        >
                          <div className="flex items-center space-x-2 flex-1">
                            <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
                              Advanced Settings
                            </Label>
                            {parallelismConfigCollapsed && (
                              <span className="text-xs text-muted-foreground">
                                {maxParallelism} GPU threads{files.find(f => f.fileName === selectedFile)?.isMultipart ? `, ${queryScope === 'first' ? 'Sample' : 'Full Dataset'}` : ''}
                              </span>
                            )}
                            {parallelismConfigCollapsed ? (
                              <ChevronRight className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </div>
                        </div>

                        {!parallelismConfigCollapsed && (
                          <div className="mt-3 space-y-4">
                            {/* GPU Threads */}
                            <div>
                              <Label className="text-xs text-gray-700 dark:text-gray-300 mb-2 block">
                                GPU Threads: {maxParallelism}
                              </Label>
                              <div className="flex items-center space-x-3">
                                <span className="text-xs text-gray-500">1</span>
                                <Slider
                                  value={[maxParallelism]}
                                  onValueChange={(value) => setMaxParallelism(value[0])}
                                  min={1}
                                  max={500}
                                  step={1}
                                  className="flex-1"
                                />
                                <span className="text-xs text-gray-500">500</span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                Controls parallel GPU thread count for processing. Default: 500. Decrease for smaller files or to reduce Lambda concurrency.
                              </p>
                            </div>

                            {/* Data Scope - Show only for large files */}
                            {files.find(f => f.fileName === selectedFile)?.isMultipart && (
                              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                                  Data Scope
                                </Label>
                                <div className="flex gap-3">
                                  <label className="flex items-center cursor-pointer">
                                    <input
                                      type="radio"
                                      name="queryScope"
                                      value="first"
                                      checked={queryScope === 'first'}
                                      onChange={(e) => setQueryScope(e.target.value as 'first' | 'all')}
                                      className="mr-2"
                                    />
                                    <div>
                                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sample</span>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">Quick preview (~1%)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-center cursor-pointer">
                                    <input
                                      type="radio"
                                      name="queryScope"
                                      value="all"
                                      checked={queryScope === 'all'}
                                      onChange={(e) => setQueryScope(e.target.value as 'first' | 'all')}
                                      className="mr-2"
                                    />
                                    <div>
                                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Full Dataset</span>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">All data</p>
                                    </div>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Search Interface - Enhanced with blue theme */}
                    <div className="p-6 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                      <label className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 block flex items-center">
                        <Search className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                        Search Dataset
                      </label>
                      <div className="flex space-x-2">
                        <Input
                          value={searchTerm}
                          onChange={handleSearchTermChange}
                          placeholder="Enter search term (e.g., 'Windows', 'John', '2024')"
                          className="flex-1 bg-white dark:bg-gray-900 border-blue-300 dark:border-blue-700 focus:border-blue-500 dark:focus:border-blue-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !isLoading) {
                              runSearch();
                            }
                          }}
                        />
                        <Button
                          onClick={runSearch}
                          disabled={isLoading || !selectedFile}
                          className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                        >
                          {isLoading ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              Searching...
                            </>
                          ) : (
                            <>
                              <Search className="h-4 w-4 mr-2" />
                              Search
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Search will look for your term across all columns in the dataset (case-insensitive)
                      </p>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </div>

            {/* Results */}
            {analysisResult && (
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center">
                        <TableIcon className="h-5 w-5 mr-2" />
                        {analysisMode === 'search' ? 'Search Results' : 'Query Results'}
                      </CardTitle>
                      {lastUsedPrompt && analysisMode === 'query' && (
                        <div className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-md border border-purple-200 dark:border-purple-700">
                          <p className="text-sm text-purple-700 dark:text-purple-200 font-medium">Query for:</p>
                          <p className="text-sm text-purple-600 dark:text-purple-300 italic">"{lastUsedPrompt}"</p>
                        </div>
                      )}
                      {analysisMode === 'search' && executedSearchTerm && (
                        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
                          <p className="text-sm text-blue-700 dark:text-blue-200 font-medium">Search term:</p>
                          <p className="text-sm text-blue-600 dark:text-blue-300 italic">"{executedSearchTerm}"</p>
                        </div>
                      )}
                      <CardDescription className="flex items-center space-x-4 mt-2">
                        <span className="flex items-center">
                          <Database className="h-4 w-4 mr-1" />
                          {analysisResult.resultTruncated && analysisResult.totalRowsAvailable ? (
                            <>
                              Showing {analysisResult.rowCount.toLocaleString()} of {analysisResult.totalRowsAvailable.toLocaleString()} rows
                            </>
                          ) : (
                            <>
                              {analysisResult.rowCount.toLocaleString()} rows
                              {analysisResult.totalRowsScanned && analysisResult.totalRowsScanned > analysisResult.rowCount && (
                                <span className="ml-1 text-xs text-muted-foreground">
                                  (scanned {analysisResult.totalRowsScanned.toLocaleString()})
                                </span>
                              )}
                            </>
                          )}
                        </span>
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {analysisResult.executionTime}ms
                        </span>
                        {analysisResult.isCached && (
                          <span className="flex items-center text-green-600 dark:text-green-400">
                            <Zap className="h-4 w-4 mr-1" />
                            Cached
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    {analysisResult.result.length > 0 && (
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={handleVisualize}>
                          <Box className="h-4 w-4 mr-2" />
                          Visualize
                        </Button>
                        <Button variant="outline" onClick={exportResults}>
                          <Download className="h-4 w-4 mr-2" />
                          {analysisResult.resultTruncated && analysisResult.totalRowsAvailable ?
                            `Export All Results (${analysisResult.totalRowsAvailable.toLocaleString()} rows)` :
                            'Export CSV'
                          }
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Partial Results Banner */}
                  {(analysisResult as any).partial_results && (
                    <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-700">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Preview Results (First Part Only)
                          </h3>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            Showing results from {(analysisResult as any).chunks_processed}/{(analysisResult as any).total_chunks_available} parts.
                            Select "All Parts" above to query the complete dataset.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pagination Controls */}
                  {analysisResult.chunkInfo && analysisResult.chunkInfo.totalChunks > 1 && (
                    <div className="mb-4 border-b pb-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      {/* View Mode Toggle */}
                      <div className="flex items-center justify-center mb-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setViewMode(viewMode === 'paginated' ? 'unified' : 'paginated')}
                        >
                          {viewMode === 'paginated' ? 'Show Unified View' : 'Show by Cluster'}
                        </Button>
                        {viewMode === 'paginated' && rowGroupResults[currentPage] && (
                          <span className="text-xs text-muted-foreground ml-3">
                            Viewing Cluster {rowGroupResults[currentPage]?.rowGroupIndex + 1} ({rowGroupResults[currentPage]?.data.length.toLocaleString()} rows)
                          </span>
                        )}
                      </div>
                      
                      {/* Cluster Selectors */}
                      {viewMode === 'paginated' && (
                        <div className="flex flex-wrap items-center justify-center gap-0.5 max-w-full">
                          {Array.from({ length: analysisResult.chunkInfo.totalChunks }, (_, idx) => {
                            const rgData = rowGroupResults.find(rg => rg.rowGroupIndex === idx);
                            const isActive = rgData !== undefined;
                            const isSelected = currentPage === rowGroupResults.findIndex(rg => rg.rowGroupIndex === idx);
                            
                            return (
                              <Button
                                key={idx}
                                variant={isSelected ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  if (isActive) {
                                    const pageIdx = rowGroupResults.findIndex(rg => rg.rowGroupIndex === idx);
                                    setCurrentPage(pageIdx);
                                  }
                                }}
                                disabled={!isActive}
                                className={`min-w-[24px] h-6 px-1.5 text-[8px] transition-all duration-200 ${
                                  isActive ? 'animate-in fade-in zoom-in-95 duration-300' : 'opacity-30'
                                }`}
                              >
                                {idx + 1}
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {analysisResult.result.length > 0 ? (
                    <EnhancedTableViewer 
                      data={viewMode === 'paginated' && rowGroupResults.length > 0 
                        ? rowGroupResults[currentPage]?.data || [] 
                        : analysisResult.result
                      } 
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {isPolling ? (
                        <div className="flex flex-col items-center space-y-2">
                          <div className="flex space-x-1">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                          </div>
                          <div className="text-sm">Processing... Results will appear as they become available.</div>
                        </div>
                      ) : (
                        <div>No data returned from query</div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Query History - Right Sidebar (Hidden in Search Mode) */}
          {analysisMode === 'query' && (
            <div className="w-[25%] flex flex-col flex-shrink-0">
              <QueryHistory 
                onQuerySelect={setQuery}
                selectedFile={selectedFile}
                refreshTrigger={queryHistoryRefreshTrigger}
              />
              
              {/* Example Queries - 30% of viewport height */}
              <div className="border-t border-border mt-auto">
                <div className="p-4 border-b border-border">
                  <h2 className="text-sm font-semibold">Example Queries</h2>
                  <p className="text-xs text-muted-foreground">
                    Click to use these query templates
                  </p>
                </div>
                <div className="p-4 overflow-y-auto" style={{ height: '30vh' }}>
                  <div className="space-y-3">
                    {exampleQueries.map((example, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setQuery(example.query);
                        }}
                        className="p-2 border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <h4 className="font-medium text-sm">{example.title}</h4>
                        <p className="text-xs text-gray-500 mt-1">{example.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Debug Modal */}
      <Dialog open={debugModalOpen} onOpenChange={setDebugModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Debug Information
            </DialogTitle>
            <DialogDescription>
              Raw response and metadata from GPT-5 query generation
            </DialogDescription>
          </DialogHeader>
          
          {selectedDebugInfo && (
            <div className="space-y-4">
              {/* Query Info */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Query Information</h3>
                <div className="bg-muted p-3 rounded-lg space-y-2">
                  <div>
                    <span className="text-xs font-medium text-gray-600">Prompt:</span>
                    <p className="text-sm">{selectedDebugInfo.prompt}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600">Status:</span>
                    <span className={`ml-2 text-sm ${selectedDebugInfo.success ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedDebugInfo.success ? 'Success' : 'Failed'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600">Timestamp:</span>
                    <span className="ml-2 text-sm">{selectedDebugInfo.timestamp.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Generated Query */}
              {selectedDebugInfo.query && (
                <div>
                  <h3 className="font-semibold text-sm mb-2">Generated Query</h3>
                  <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto">
                    <code>{selectedDebugInfo.query}</code>
                  </pre>
                </div>
              )}

              {/* Debug Information */}
              {selectedDebugInfo.debug && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">GPT-5 Debug Information</h3>
                  
                  {/* Model Info */}
                  <div>
                    <h4 className="font-medium text-xs text-gray-600 mb-1">Model & Parameters</h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-xs space-y-1">
                      <div><span className="font-medium">Model:</span> {selectedDebugInfo.debug.model}</div>
                      <div><span className="font-medium">Finish Reason:</span> {selectedDebugInfo.debug.finish_reason}</div>
                      <div><span className="font-medium">Validation:</span> {selectedDebugInfo.debug.validation_passed ? 'Passed' : 'Failed'}</div>
                      {selectedDebugInfo.debug.error_type && (
                        <div><span className="font-medium">Error Type:</span> {selectedDebugInfo.debug.error_type}</div>
                      )}
                    </div>
                  </div>

                  {/* Token Usage */}
                  {selectedDebugInfo.debug.token_usage && (
                    <div>
                      <h4 className="font-medium text-xs text-gray-600 mb-1">Token Usage</h4>
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-xs space-y-1">
                        <div><span className="font-medium">Total Tokens:</span> {selectedDebugInfo.debug.token_usage.total_tokens}</div>
                        <div><span className="font-medium">Prompt Tokens:</span> {selectedDebugInfo.debug.token_usage.prompt_tokens}</div>
                        <div><span className="font-medium">Completion Tokens:</span> {selectedDebugInfo.debug.token_usage.completion_tokens}</div>
                        {selectedDebugInfo.debug.token_usage.completion_tokens_details?.reasoning_tokens && (
                          <div><span className="font-medium">Reasoning Tokens:</span> {selectedDebugInfo.debug.token_usage.completion_tokens_details.reasoning_tokens}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Raw Response */}
                  <div>
                    <h4 className="font-medium text-xs text-gray-600 mb-1">Raw GPT-5 Response</h4>
                    <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto max-h-40">
                      <code>{selectedDebugInfo.debug.raw_response}</code>
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}; 